<template>
    <RegularModal ref="modal" @closed="onClose">
        <template v-slot:title>{{ translate("page.admin_edit_event.choose_location") }}</template>
        <template v-slot:body>
            <div class="columns event-location-modal">
                <div class="column">
                    <div class="field">
                        <label class="label">{{ translate("manage_academy.country") }}</label>
                        <CountrySelector ref="countrySelector" @countrySelected="onCountrySelected" />
                    </div>

                    <div class="field">
                        <label class="label">{{ translate("page.admin_edit_event.place") }}</label>
                        <input class="input" v-model="place" maxlength="100" />
                    </div>
                    <label class="label">{{ translate("page.admin_edit_event.map_coordinates") }}</label>
                    <div class="field">
                        <p class="control" :class="{ 'has-error': !validLocation }">
                            <input
                                type="text"
                                :value="locationInput"
                                @input="e => (locationInput = (e.target as HTMLInputElement)?.value)"
                                class="input"
                                maxlength="100"
                                :placeholder="`${translate('page.admin_edit_event.latitude')} + ${translate('page.admin_edit_event.longitude')}`"
                            />
                            <span class="help" v-if="!validLocation">
                                {{ translate("page.admin_edit_event.must_be_valid_geo_location_string") }}
                            </span>
                        </p>
                    </div>
                    <div class="field">
                        <Accordion>
                            <template v-slot:title>
                                {{ translate("page.admin_edit_event.how_to_get_location") }}
                            </template>
                            <template v-slot:content>
                                {{ translate("page.admin_edit_event.help_get_location") }}
                            </template>
                        </Accordion>
                    </div>
                    <div class="field is-grouped">
                        <p class="control">
                            <button class="button" @click="refreshMap" :disabled="!validGeoLocation">
                                <span class="icon">
                                    <i class="fa fa-sync" />
                                </span>
                                <span>{{ translate("page.admin_edit_event.refresh_map") }}</span>
                            </button>
                        </p>
                        <p class="control">
                            <button class="button" @click="clearCoordinates">
                                <span class="icon">
                                    <i class="fa fa-times" />
                                </span>
                                <span>{{ translate("common.clear") }}</span>
                            </button>
                        </p>
                    </div>
                    <div class="field">
                        <iframe v-if="!!url" class="map-box" :src="url" frameborder="0"></iframe>
                        <div v-else class="map-box is-flex is-align-items-center is-justify-content-center">
                            <div class="has-text-centered is-italic">
                                <p>{{ translate("page.admin_edit_event.enter_coordinates") }}</p>
                                <p>{{ translate("page.admin_edit_event.map_visible_only") }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <div class="is-flex is-flex-grow-1 is-align-items-center is-justify-content-space-between">
                <p class="has-text-warning">
                    <span class="icon">
                        <i class="fas fa-exclamation-triangle" />
                    </span>
                    <span>
                        {{ translate("page.admin_edit_event.remember_to_save") }}
                    </span>
                </p>
                <div class="button" @click="close">{{ translate("common.close") }}</div>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed, watch } from "vue";
    import { ComponentExposed } from "vue-component-type-helpers";
    import RegularModal from "../../Common/RegularModal.vue";
    import throttle from "lodash.throttle";
    import { DateTime } from "luxon";
    import Accordion from "../../Common/Accordion.vue";
    import { translate } from "../../Common/translations";
    import urls from "../../Urls/urls";
    import { useModal } from "../../Common/useModal";
    import { EventDate } from "../../Common/types";
    import CountrySelector from "../../EventRegistration/CountrySelector.vue";
    import { useEditEventStore } from "../editEventStore";

    /**
     * TODO: This modal should have Cancel/Save buttons and save the data to the backend. Don't expect from users to close the window and hit save in main form. It's kind of context switching.
     */

    const store = useEditEventStore();

    const eventDate = computed<EventDate>(() => store.event.eventDate);

    const { open: openModal, modal, close } = useModal();

    const open = () => {
        // @ts-expect-error - latitude and longitude are added dynamically //TODO: Handle it internally here, not in store. Store shoud just have geoLocation.
        localLat.value = store.event.latitude || "";
        // @ts-expect-error - latitude and longitude are added dynamically
        localLng.value = store.event.longitude || "";

        if (localLat.value !== "" && localLng.value !== "") {
            locationInput.value = `${localLat.value},${localLng.value}`;
        } else {
            locationInput.value = "";
        }

        url.value = "";

        if (countrySelector.value) {
            countrySelector.value.init(store.event.country);
        }

        openModal();
    };

    defineExpose({ open, close });

    // PLACE

    const place = computed({
        get() {
            return store.event.place;
        },
        set(value) {
            store.setEventProperty({ name: "place", value });
        },
    });

    // COUNTRY

    const countrySelector = ref<ComponentExposed<typeof CountrySelector>>();

    function onCountrySelected(country: string) {
        store.setEventProperty({ name: "country", value: country });
    }

    // HANDLING COORDINATES

    const locationInput = ref<string>("");
    const localLat = ref<string>("");
    const localLng = ref<string>("");

    const validLocation = computed(() => {
        if (locationInput.value === "") {
            return true;
        }

        return isCoordinatesValue(locationInput.value);
    });

    const validGeoLocation = computed<boolean>(() => {
        const lat = parseInt(localLat.value);
        const lng = parseInt(localLng.value);
        return lat < 90 && lat > -90 && lng < 180 && lng > -180;
    });

    const clearCoordinates = (): void => {
        localLat.value = "";
        localLng.value = "";
        url.value = getUrl();
    };

    function isCoordinatesValue(coordinatesString: string): boolean {
        const coordinates = coordinatesString.split(",");
        if (coordinates.length !== 2) {
            return false;
        }
        const lat = parseInt(coordinates[0]);
        const lng = parseInt(coordinates[1]);
        return lat < 90 && lat > -90 && lng < 180 && lng > -180;
    }

    function handleLocationChange() {
        if (locationInput.value === "") {
            localLat.value = "";
            localLng.value = "";
            return;
        }
        if (!isCoordinatesValue(locationInput.value)) {
            localLat.value = "";
            localLng.value = "";
            return;
        }

        const [lat, lng] = locationInput.value.split(",");

        localLat.value = lat;
        localLng.value = lng;
    }

    watch(locationInput, handleLocationChange);

    const onClose = () => {
        store.setEventProperty({ name: "latitude", value: localLat.value });
        store.setEventProperty({ name: "longitude", value: localLng.value });
    };

    // MAP

    const url = ref<string>("");

    const getUrl = (): string => {
        if (localLat.value === "" || localLng.value === "") {
            return "";
        }
        const eventStart = DateTime.fromSQL(eventDate.value.startDate, { zone: "UTC" }).toUnixInteger();
        const eventEnd = DateTime.fromSQL(eventDate.value.endDate, { zone: "UTC" }).toUnixInteger();
        return urls.eventRelated().eventMapUrl(localLat.value, localLng.value, eventStart, eventEnd);
    };

    const refreshMap = throttle(() => {
        url.value = getUrl();
    }, 1000);
</script>

<style scoped></style>
